import { useState } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { getPostLoginRoute, ROUTES } from '@/constants/routes';
import logoImage from '@/assets/thinkotter-logo.png';

interface AppLogoProps {
  className?: string;
  /**
   * BUG-20260701-230408 (RC-002): Optional active session id. When provided AND the
   * authenticated user is a student (not a parent), the logo navigates to
   * `/session/{resumeSessionId}` instead of the default post-login route, preserving
   * session context so clicking the logo mid-session does not abandon the active
   * session and fall through to the "start a new session" funnel. Backward
   * compatible: when absent, behaviour is byte-for-byte unchanged. Only
   * SessionChatPage passes this prop today - 2026-07-02
   */
  resumeSessionId?: string;
}

export function AppLogo({ className, resumeSessionId }: AppLogoProps) {
  const [hasError, setHasError] = useState(false);
  const { isAuthenticated, user } = useAuth();

  // Route the logo to the user's OWN dashboard rather than the deprecated static
  // /dashboard for everyone: parents -> /parent, students -> /dashboard. Delegates
  // the parent/student fork to getPostLoginRoute (the same helper used by
  // RootRedirect / LoginPage), so the precedence rule stays in one place. An
  // unauthenticated visitor falls back to login.
  //
  // BUG-20260701-230408 (RC-002): when resumeSessionId is supplied and the user is
  // a student, resume the in-progress session instead - 2026-07-02
  const isStudent = !(user?.groups ?? []).includes('parent');
  const logoTarget = isAuthenticated
    ? resumeSessionId && isStudent
      ? `/session/${resumeSessionId}`
      : getPostLoginRoute(user?.groups)
    : ROUTES.LOGIN;

  const logo = hasError ? (
    <span className={cn('text-lg md:text-xl font-bold', className)}>ThinkOtter</span>
  ) : (
    <img
      src={logoImage}
      alt="ThinkOtter"
      onError={() => setHasError(true)}
      className={cn('h-8 md:h-10 w-auto object-contain', className)}
    />
  );

  return <Link to={logoTarget}>{logo}</Link>;
}
